// @flow
import React, { Component, type Element } from "react";
import { noop, isEqual, filter, get } from "lodash";
import type {
  NullaryFn,
  BinaryFn,
  UnaryFn,
  TernaryFn
} from "@tvg/types/Functional";
import type { HelpTypeEnum } from "@fdr/types/ProgramPage";
import type { WagerGroupEnum } from "@fdr/types/WagerTypes";

import type {
  RaceCardBettingInterest,
  RaceTypeCodeEnum,
  RaceCardRunner,
  RacePanelLink,
  RaceProgram,
  PastPerformanceRace,
  RunnerTagsModalProps
} from "@tvg/types/Race";
import RunnerLine from "../../_molecules/RunnerLine";

export type Selections = string[][];

type SilksErrorProps = { current: { [number]: boolean } | null };

type Props = {
  runners: RaceCardBettingInterest[],
  /**
   * Leg number for the race
   */
  leg: number,
  /**
   * Flag to activate the leg layout
   */
  isLeg: boolean,
  /**
   * Flag that controls if the race is wagerable or not
   */
  isWagerable: boolean,
  /**
   * Race type code
   */
  raceTypeCode: RaceTypeCodeEnum,
  /**
   * Array with the current selections of the RaceCard
   */
  selections: Selections,
  /**
   * Number of selection columns
   */
  numColumns: number,
  /**
   * Calback to run when user selects a runner
   */
  onRunnerSelect: BinaryFn<string, number, mixed>,
  /**
   Reveals if the user may see the inLinePastPerformance or be redirected to the race card on the respective runnerLines
  */
  redirectToRaceCard: boolean,
  /**
   The race url to be redirected to
  */
  raceUrl: string,
  /**
   * On runner selection for top races
   */
  onRunnerSelection: TernaryFn<RacePanelLink, number, string, void>,
  /**
   * The Race this runner list is on
   */
  race: ?RacePanelLink,
  /**
   * hover effect on runner bio
   *
   */
  hasHover: boolean,
  /*
   * If is an Sky Racing World race
   */
  isSRW: boolean,
  /*
   * If it is Racing and Sports race
   */
  isRAS: boolean,
  /*
   * Source of Runner Inline commentary
   */
  freePickSource: string,
  onSelectAllRunnerEntries: UnaryFn<string, void>,
  isLogged: boolean,
  expandedInfo: TernaryFn<number, number, boolean, Element<*> | void>,
  changeUpdate: TernaryFn<string, number, boolean, void>,
  selectedRunners: string[][],
  openHelpModal: UnaryFn<HelpTypeEnum, void>,
  isNumberFireSection: boolean,
  currentRace: ?RaceProgram | ?PastPerformanceRace,
  showWinProb: boolean,
  betAmount: number,
  group: ?WagerGroupEnum,
  fullWidth: boolean,
  showSilks: boolean,
  internationalLabel: string,
  shouldShowFirstOnboarding: boolean,
  onTooltipClick: NullaryFn<void>,
  setOnboardingStatus: BinaryFn<string, string, void>,
  useGraphStatusV2?: boolean,
  bettingInterests?: Array<Array<RaceCardBettingInterest>>,
  showRunnerUpdate: boolean,
  enableNewRacePage: boolean,
  wagerTypeCode?: string,
  onRaceTagsInfoClick: UnaryFn<RunnerTagsModalProps, void>,
  selectAllButtonLabel?: string
};

export const getRunnerSelections = (biNumber: number, selections: Selections) =>
  // $FlowFixMe
  selections.map(
    (column) => (column ? column.indexOf(biNumber.toString()) : -1) >= 0
  );

export default class RunnerList extends Component<Props> {
  static defaultProps = {
    runners: [],
    leg: 0,
    isLeg: false,
    isWagerable: true,
    onRunnerSelect: noop,
    raceTypeCode: "T",
    selections: [[]],
    numColumns: 1,
    redirectToRaceCard: false,
    raceUrl: "/",
    onRunnerSelection: noop,
    race: null,
    hasHover: false,
    onSelectAllRunnerEntries: noop,
    isLogged: false,
    expandedInfo: noop,
    changeUpdate: noop,
    selectedRunners: [[]],
    openHelpModal: noop,
    currentRace: null,
    isNumberFireSection: false,
    showWinProb: true,
    betAmount: 0,
    isSRW: false,
    isRAS: false,
    freePickSource: "handicapping",
    group: null,
    fullWidth: false,
    showSilks: false,
    internationalLabel: "Int'l terms",
    shouldShowFirstOnboarding: false,
    onTooltipClick: noop,
    setOnboardingStatus: noop,
    useGraphStatusV2: false,
    bettingInterests: [],
    showRunnerUpdate: false,
    enableNewRacePage: false,
    onRaceTagsInfoClick: noop,
    selectAllButtonLabel: "Box"
  };

  silksError: SilksErrorProps = React.createRef();

  constructor(props: Props) {
    super(props);
    this.silksError.current = {};
  }

  shouldComponentUpdate(nextProps: Props) {
    return !isEqual(nextProps, this.props);
  }

  isRunnerOpen = (runner: RaceCardRunner, index: number) => {
    if (this.props.selectedRunners[this.props.leg]) {
      const isOpen = !!this.props.selectedRunners[this.props.leg].find(
        (item) => item === runner.runnerId
      );

      if (isOpen && index === 0)
        this.props.setOnboardingStatus("runner-info", "click");

      return isOpen;
    }

    return false;
  };

  renderRunners() {
    const flatBi = this.props.runners.reduce(
      (acc, bi: RaceCardBettingInterest) => {
        const runners = bi.runners
          ? bi.runners.map(
              (
                runner: RaceCardRunner
              ): {
                bi: RaceCardBettingInterest,
                runner: RaceCardRunner
              } => ({ bi, runner })
            )
          : [];
        return acc.concat(runners);
      },
      []
    );
    const freePickSourceNumber = `runner.${this.props.freePickSource}.freePick.number`;

    const flatBiFiltered = flatBi.filter((bi) => {
      const freePickNumber = get(bi, freePickSourceNumber, null);
      return this.props.isNumberFireSection ? freePickNumber !== null : true;
    });

    const runnersWithValidSilks = flatBi.reduce(
      (accValidRunners, currentRunner) => {
        const { timeform } = currentRunner?.runner ? currentRunner.runner : {};

        if (timeform) {
          const { silkUrl, silkUrlSvg } = timeform;

          if (silkUrl !== null || silkUrlSvg !== null) {
            accValidRunners.push(currentRunner.runner);
          }
        }
        return accValidRunners;
      },
      []
    );

    const numberOfrunnersWithSilksError =
      this.silksError.current && Object.keys(this.silksError.current).length;

    // some runners dont have silksError and dont have valid silks from timeform
    const allRunnersHasSilksError =
      (runnersWithValidSilks.length === 0 &&
        numberOfrunnersWithSilksError === 0) ||
      numberOfrunnersWithSilksError === flatBiFiltered.length;

    return flatBiFiltered
      .sort((a, b) => {
        if (this.props.isNumberFireSection) {
          return (
            get(a, freePickSourceNumber, 0) - get(b, freePickSourceNumber, 0)
          );
        }
        return a.runner.scratched
          ? a.bi.biNumber - (b.runner.scratched ? b.bi.biNumber : 0)
          : 0;
      })
      .map((element, index) => {
        const bi = element?.bi;
        const runner = element?.runner;
        if (!bi || !runner) {
          return null;
        }
        const isCoupled =
          filter(
            this.props.runners,
            (biRunner) => biRunner.biNumber === bi.biNumber
          ).length >= 2;

        return (
          <RunnerLine
            onRaceTagsInfoClick={this.props.onRaceTagsInfoClick}
            openHelpModal={this.props.openHelpModal}
            isOpen={this.isRunnerOpen(runner, index)}
            changeUpdate={this.props.changeUpdate}
            expandedInfo={this.props.expandedInfo}
            isLogged={this.props.isLogged}
            race={this.props.race}
            currentRace={this.props.currentRace}
            runner={runner}
            runners={this.props.runners}
            bettingInterest={bi}
            isCoupled={isCoupled}
            key={`runner-${runner.runnerId}${
              this.props.isNumberFireSection ? "isNumberFireSection" : ""
            }`}
            selections={getRunnerSelections(bi.biNumber, this.props.selections)}
            raceTypeCode={this.props.raceTypeCode}
            isLeg={this.props.isLeg}
            leg={this.props.leg}
            isWagerable={this.props.isWagerable}
            onRunnerSelect={this.props.onRunnerSelect}
            numColumns={this.props.numColumns}
            onRunnerSelection={this.props.onRunnerSelection}
            redirectToRaceCard={this.props.redirectToRaceCard}
            raceUrl={this.props.raceUrl}
            hasHover={this.props.hasHover}
            onSelectAllRunnerEntries={this.props.onSelectAllRunnerEntries}
            isNumberFireSection={this.props.isNumberFireSection}
            showWinProb={this.props.showWinProb}
            betAmount={this.props.betAmount}
            isSRW={this.props.isSRW}
            isRAS={this.props.isRAS}
            freePickSource={this.props.freePickSource}
            group={this.props.group}
            fullWidth={this.props.fullWidth}
            showSilks={this.props.showSilks && !allRunnersHasSilksError}
            internationalLabel={this.props.internationalLabel}
            index={index}
            shouldShowFirstOnboarding={this.props.shouldShowFirstOnboarding}
            onTooltipClick={this.props.onTooltipClick}
            setOnboardingStatus={this.props.setOnboardingStatus}
            useGraphStatusV2={this.props.useGraphStatusV2}
            bettingInterests={this.props.bettingInterests}
            showRunnerUpdate={this.props.showRunnerUpdate}
            enableNewRacePage={this.props.enableNewRacePage}
            wagerTypeCode={this.props.wagerTypeCode}
            selectAllButtonLabel={this.props.selectAllButtonLabel}
            handleError={(runnerNumber) => {
              this.silksError.current = {
                ...this.silksError.current,
                [runnerNumber]: true
              };
            }}
          />
        );
      });
  }

  render() {
    return !!this.props.runners && this.renderRunners();
  }
}
